// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 63.23.0 — where the login page may send a person after sign-in.
 *
 * The OAuth consent page sends a signed-out visitor to `/login?next=…` and
 * expects to be returned to. A `next` that is not a same-origin PATH would make
 * the login page an open redirect — `//evil.example`, `https://…`,
 * `/\evil.example` (browsers treat a backslash as a slash) and
 * `javascript:` all name somewhere else. Only an absolute path on this origin
 * passes; anything else is `null` and the caller keeps its default.
 */
export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//') || raw.includes('\\')) return null;
  // Control characters (tab, newline) are stripped by URL parsers and can turn
  // `/\t/evil.example` into `//evil.example`.
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null;
  try {
    /*
     * No origin comparison follows the parse, deliberately (gate E12.5 pass 1,
     * P2-2). Once `raw` starts with one `/`, has no second `/` or backslash
     * after it and no control character, it parses as a path relative to the
     * base and its origin IS the base — the comparison could not fail, so it
     * was a guard no input could make red. The checks above are what stop
     * another host, and each has a case in the test that only it refuses.
     */
    const url = new URL(raw, 'https://same-origin.invalid');
    const path = `${url.pathname}${url.search}${url.hash}`;
    /*
     * Gate E12.5 pass 1 (P2-1): the raw-string checks read the input BEFORE
     * normalisation, and dot segments survive them — `/.//evil.com`,
     * `/..//evil.com`, `/%2e//evil.com` and `/a/..//evil.com` all normalise to
     * `//evil.com`, which a browser resolves to another host. So the RESULT is
     * held to the same rule. A backslash cannot appear here: the input check
     * above refuses one, and the URL parser writes it as `/` for an https base
     * and keeps an encoded `%5C` encoded — so `//` is the whole condition.
     */
    if (path.startsWith('//')) return null;
    return path;
  } catch {
    return null;
  }
}
